import { Utils } from '@genial/utils';
import { isArray } from 'lodash';

import {
  CustomerPlatformsItem,
  CustomerTiesItem,
  TradingCustomerProfileItem,
  TradingDataConsolidatedDTO,
} from './types';

const normalizePersonType = (type?: string) => {
  if (!type) {
    return '--';
  }

  return type === 'F' ? 'Física' : 'Jurídica';
};

const normalizeAccountWithDigit = (account: number | string) => {
  if (!account) {
    return '--';
  }

  const accountString = account.toString();
  const hyphenIndex = accountString.length - 1;

  return accountString.substring(0, hyphenIndex) + '-' + accountString.substring(hyphenIndex);
};

const normalizeInitials = (name: string) => {
  if (!name) {
    return '--';
  }

  const nameAsArray = name.split(' ');

  if (nameAsArray.length === 1) {
    return nameAsArray[0].substring(0, 2).toUpperCase();
  }

  const initials = nameAsArray.map((word) => word.charAt(0).toUpperCase());

  return `${initials[0]}${initials[initials.length - 1]}`;
};

export const normalizeTradingCustomerProfileResponse = (data: any): TradingCustomerProfileItem => ({
  contaBPComDigito: normalizeAccountWithDigit(data?.contaBPComDigito),
  contaSinacorComDigito: normalizeAccountWithDigit(data?.contaSinacorComDigito),
  contaSuperPNPComDigito: normalizeAccountWithDigit(data?.contaSuperPNPComDigito),
  contaSinacorSemDigito: data?.contaSinacorSemDigito?.toString() || '--',
  contaSuperPNPSemDigito: data?.contaSuperPNPSemDigito?.toString() || '--',
  idConta: data?.idConta?.toString() || '--',
  email: data?.email || '--',
  nomeCliente: data?.nomeCliente || '--',
  initials: normalizeInitials(data?.nomeCliente),
  tipoPessoa: data?.tipoPessoa || '--',
  tipoPessoaNormalized: normalizePersonType(data?.tipoPessoa),
  promotionalRestriction: {
    hasRestriction: !!data?.promotionalRestriction?.blackListExpirationDate,
    blackListExpirationDate: data?.promotionalRestriction?.blackListExpirationDate,
    promotionalStatus: data?.promotionalRestriction?.promotionalStatus,
    statusText: data?.promotionalRestriction?.blackListExpirationDate ? 'Restrito' : 'Liberado',
  },
});

export const normalizeCustomerTradingList = (data: object): TradingCustomerProfileItem[] => {
  if (!isArray(data)) {
    return [];
  }

  return data.map((item) => ({
    ...normalizeTradingCustomerProfileResponse(item.investorProfile),
  }));
};

export const normalizeTradingCustomerTiesResponse = (data: object): CustomerTiesItem[] => {
  if (!isArray(data)) {
    return [];
  }

  return data.map((item) => ({
    dataAdesao: item?.dataAdesao || '',
    nomeProduto: item?.nomeProduto || '--',
    status: 'Ativo'.toUpperCase(),
  }));
};

export const normalizeTradingCustomerPlatformsResponse = (
  data: object
): CustomerPlatformsItem[] => {
  if (!isArray(data)) {
    return [];
  }

  return data.map((item) => ({
    toolName: item?.toolName || '--',
    packageName: item?.packageName || '--',
    hiringDate: item?.hiringDate || '',
    cancelDate: item?.cancelDate || '',
    hiringStateDescription: (item?.hiringStateDescription || '--').toUpperCase(),
  }));
};

export const normalizeTradingConsolidate = (
  tradingDataConsolidadoDTO: TradingDataConsolidatedDTO[] | null
) =>
  tradingDataConsolidadoDTO && tradingDataConsolidadoDTO.length
    ? tradingDataConsolidadoDTO.map((tradingDataConsolidate) => ({
        date: tradingDataConsolidate.data
          ? Utils.Mask.DateFormat(tradingDataConsolidate.data, 'dd/MM/yyyy')
          : '--',
        operation: tradingDataConsolidate.operacao || '--',
        paper: tradingDataConsolidate.papel || '--',
        position: tradingDataConsolidate.posicao !== null ? tradingDataConsolidate.posicao : '--',
        purchaseQty: tradingDataConsolidate.qtdCompra || '--',
        executedQty: tradingDataConsolidate.qtdExecutada || '--',
        saleQty: tradingDataConsolidate.qtdVenda || '--',
        tradings:
          tradingDataConsolidate.tradingDataDTO && tradingDataConsolidate.tradingDataDTO.length
            ? tradingDataConsolidate.tradingDataDTO.map((tradingData) => ({
                operation: tradingData.operacao || '--',
                executedQty: tradingData.cumQty || '--',
                executedPrice:
                  tradingData.price !== null ? Utils.Mask.Money(tradingData.price) : '--',
                market: tradingData.market || '--',
                status: tradingData.statusDetail || '--',
              }))
            : [],
      }))
    : [];
